import Backbone from 'backbone'
import template from 'scoreboard/datasets/filters/label-clusters-form.hbs'
import FormView from 'scoreboard/components/form-view'
import formHelper from 'scoreboard/components/form-helper'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import formatValue from 'scoreboard/components/format-value'

function defaultClusterLabel(index) {
	return i18n.get('datasets.field.clusterFields.label.default.withArg', index+1)
}

const LabelClustersForm = Backbone.View.extend({

	tagName: 'form',

	/** @type {DatasetField} */
	field: null,

	/** @type {Dataset} */
	model: null,

	/**
	 * @typedef {{
	 * 		bestNumClusters: number,
	 * 		summaries: {
	 * 			numClusters: number,
	 * 			score: number
	 * 		}
	 * 	}} ClusterResponse
	 */

	/**
	 * @param options
	 * @param {DatasetField} options.field
	 * @param {ClusterResponse} options.clusterResponse response from
	 * api/datasets/{this.model.id}/clusters
	 */
	initialize: function(options) {
		this.options = options
		this.field = options.field
		this.clusterResponse = options.clusterResponse
		this.field.numClusters = this.field.numClusters ||
			this.clusterResponse.bestNumClusters

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			guard: false,
			allowCleanSubmission: true
		})
	},

	render: function() {
		const fieldsById = this.model.combinedFieldsById
		const clusterFields = this.field.clusteredFieldIds.map(it => fieldsById[it])
		const summary =
			// Cluster summaries start at two - which puts everything back by one.
			// Then we account for the fact that the list is zero-based, and subtract
			// one more to find the index with the effective number of clusters,
			// (selected or default).
			this.clusterResponse.summaries[this._numClusters() -2]

		summary.clusters.forEach(cluster => {
			cluster.fieldCenters = clusterFields.map(field => {
				return {
					center: formatValue({
						value: cluster.center[field.id],
						precision: 0
					})
				}
			})
			cluster.score = formatValue({ value: cluster.score, precision: 0 })
		})

		this.$el.empty().append(template({
			summary,
			fields: clusterFields
		}))

		this.options.wizard.render({
			el: this.el,
			stateSaver: () => this.saveState()
		})

		this._writeClusterLabelsOrDefaults()

		this.formView.start()
	},

	_writeClusterLabelsOrDefaults: function() {
		const state = _.fromPairs(_.range(0, this._numClusters())
			.map(index => {
				let label = this.model.getClusterLabel(this.field, index)
				return [`label${index}`, label]
			}))

		formHelper.writeToDom(state, this.el)
	},

	_numClusters: function() {
		if (this.field.numClusters) {
			return this.field.numClusters
		} else {
			return _.maxBy(this.clusterResponse.summaries,
				it => it.score).numClusters
		}
	},

	saveState: function() {
		const fromDom = formHelper.readFromDom(this.el)
		const clusterLabels = _.values(fromDom)
			.map((val, index) => {
				return {
					index,
					label: val
				}
			})
			// #42200 filter operation must take place *after* map operation, or the
			// indices will be offset by one for every [default] label that is
			// filtered out.
			.filter(({ index, label }) => {
				// Don't bother saving the defaults; they'll just be regenerated next
				// time we load
				return label !== defaultClusterLabel(index)
			})

		_.extend(this.field, { clusterLabels })
	},

	submit: function(e) {
		this.saveState()
		this.trigger('done', this.field)
		this.options.wizard.finish(this.field)
		return false
	}
})

export default LabelClustersForm
export { defaultClusterLabel }
LabelClustersForm.__filename__ = 'datasets/filters/label-clusters-form.js'
